import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  SetAccountSessionMutation,
  SetAccountSessionMutationVariables,
  UserAccountListQuery,
  UserIraAccountBalancesQuery,
} from "types/graphql";

// get users financial accounts
function getUserAccounts(
  client: ApolloClient<object>
): Promise<UserAccountListQuery["userAccountList"]> {
  return new Promise((resolve, reject) => {
    const GET_ALL_ACCOUNTS = gql`
      query userAccountList {
        userAccountList {
          bankAccounts {
            id
            bankName
            bankAlias
            verificationStatus
            accountType
            accountSubtype
            accountId
            correlationId
            state
            loginRequired
            recurringContribution {
              frequency
              anchorPayDate
              amount
              dayOne
              dayTwo
              sourceBankAccountId
              targetIraAccountId
              nextScheduledDate
              scheduleId
            }
          }
          iraAccounts {
            id
            routingNumber
            accountNumber
            name
            accountType
            isRollover
            state
            createdAt
            active
            lifecycleState
            isProcessingRollover
            balances {
              withdrawalBalance
              totalAccountBalance
              cashBalance
              principal
              totalPendingContributions
              totalPendingDistributions
            }
            rollovers {
              amount
              status
              accountId
              docId
              accountNumber
              createdAt
              updatedAt
              originationSource
              financialInstitution
              fundsTransferMethod
            }
          }
        }
      }
    `;
    const result = client.query<UserAccountListQuery>({
      query: GET_ALL_ACCOUNTS,
      variables: {},
      fetchPolicy: "network-only",
    });
    result.then(
      (data) => {
        resolve(data.data.userAccountList);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getIraAccountBalanceRequest(
  client: ApolloClient<object>,
  iraAccountId: string
): Promise<UserIraAccountBalancesQuery["userAccountList"]> {
  return new Promise((resolve, reject) => {
    const query = gql`
      query userIraAccountBalances {
        userAccountList {
          iraAccounts {
            id
            balances {
              withdrawalBalance
              totalAccountBalance
              cashBalance
              principal
              totalPendingContributions
              totalPendingDistributions
            }
          }
        }
      }
    `;
    const result = client.query<UserIraAccountBalancesQuery>({
      query,
      variables: { iraAccountId },
      fetchPolicy: "network-only",
    });
    result.then(
      (data) => {
        resolve(data.data.userAccountList);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function setAccountSession(
  client: ApolloClient<object>,
  accountId: string
): Promise<SetAccountSessionMutation["setAccountSession"] | null> {
  return new Promise((resolve, reject) => {
    const request = gql`
      mutation setAccountSession($accountId: ID!) {
        setAccountSession(accountId: $accountId)
      }
    `;

    const result = client.mutate<
      SetAccountSessionMutation,
      SetAccountSessionMutationVariables
    >({
      mutation: request,
      variables: {
        accountId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.setAccountSession ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export const accountService = {
  getUserAccounts,
  getIraAccountBalanceRequest,
  setAccountSession,
};
